import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { LightbulbOutlined as LightbulbOutlinedIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { api } from '../../api/apiClient';
import { MarkdownView } from '../../components/MarkdownView';
import { usePageMeta } from '../../hooks/usePageMeta';
import { SubscribeCard } from '../../components/SubscribeCard';

interface TipOfTheDay {
    tipId: number;
    title: string;
    date: string;
    tipContent: string;
    domain: string;
}

const TIP_DOMAIN = 'General';

function formatDate(date: string) {
    return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function TipOfTheDayPage() {
    usePageMeta({
        title: 'Tip of the Day',
        description: 'A daily tip from ColbrenSolutions on AI, automation, and practical technology for small businesses.',
    });

    const [tip, setTip] = useState<TipOfTheDay | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        api.get(`/api/tips/today?domain=${TIP_DOMAIN}`)
            .then(r => {
                if (!r.ok) throw new Error('Failed to load tip');
                return r.json();
            })
            .then((data: TipOfTheDay) => setTip(data))
            .catch(() => setError('Failed to load today\'s tip'))
            .finally(() => setLoading(false));
    }, []);

    return (
        <Box sx={{ pt: 3, maxWidth: 800 }}>
            <SubscribeCard domain={TIP_DOMAIN} appName='Colbren Solutions UI' />

            {loading && <CircularProgress size={24} />}
            {error && <Typography color='error'>{error}</Typography>}

            {!loading && !error && !tip && (
                <Typography color='text.secondary'>No tip available yet — check back soon.</Typography>
            )}

            {!loading && !error && tip && (
                <Stack direction='row' spacing={2} alignItems='flex-start'>
                    <LightbulbOutlinedIcon sx={{ fontSize: 40, color: 'warning.main', mt: 0.5 }} />
                    <Box>
                        <Typography variant='h4' fontWeight={700} sx={{ mb: 0.5 }}>
                            {tip.title}
                        </Typography>
                        <Typography variant='subtitle1' color='text.secondary' sx={{ mb: 2 }}>
                            {formatDate(tip.date)}
                        </Typography>
                        <MarkdownView>{tip.tipContent}</MarkdownView>
                    </Box>
                </Stack>
            )}
        </Box>
    );
}
